import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Box, CircularProgress, Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';

import { CustomButton } from '../../../components/customButton/CustomButton';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { RenderInputByTypeMobile } from '../../../components/formGroupGenerator/RenderInputByTypeMobile';
import { GroupedContainer } from '../../ContratosCreation/components/GroupedContainer';

import { IconButton, Tooltip } from '@mui/material';
import {
  CREATE_HONORARIO_FIDELIZADO,
  UPDATE_HONORARIO_FIDELIZADO,
  buildCreateInput,
  buildEditInput,
} from '../../../data/graphQL/mutations/HonorarioFidelizado/honorarioFidelizadoMutations';
import {
  GET_HONORARIOS_FIDELIZADOS,
  adaptHonorarioFidelizado,
} from '../../../data/graphQL/queries/HonorarioFidelizado/getHonorariosFidelizados';
import adaptFiguraProfesionalArray, { GET_ALL_FIGURA_PROFESIONAL } from '../../../data/graphQL/queries/Honorarios/getFigurasProfesional';
import adaptClasificacionesMonitoreoArray, { GET_ALL_CALIFICACIONES_MONITOREO } from '../../../data/graphQL/queries/Honorarios/getCalificacionesMonitoreo';
import { GET_ROLES_BY_USER } from '../../../data/graphQL/queries/Reclamos/getRolesByUserId';
import { SelectorProfesionalesDialog } from './SelectorProfesionalesDialog';

const inputGroups = [
  {
    fields: [
      { displayName: 'Descripción', name: 'descripcion', required: true, type: 'string', col: 6 },
      { displayName: 'Delegación', name: 'idDelegacion', required: true, type: 'select', col: 6 },
      { displayName: 'Tipo profesional', name: 'idTipoProfesional', required: true, type: 'select', col: 3 },
      { displayName: 'Figura profesional', name: 'idFiguraProfesional', required: true, type: 'select', col: 3 },
      { displayName: 'Clase monitoreo', name: 'idClaseMonitoreo', required: true, type: 'select', col: 3 },
      { displayName: 'Importe', name: 'importe', required: true, type: 'currency', min: 0, col: 1.5 },
      { displayName: 'Fecha expira', name: 'fechaExpira', required: true, type: 'date', col: 1.8 },
      { displayName: 'Activo', name: 'activo', required: true, type: 'custom_switch', col: 1.5 },
    ],
  },
  {
    name: 'Observación',
    fields: [
      { displayName: 'Observación', name: 'observacion', required: false, type: 'long_string', col: 100 },
    ],
  },
];

// ── Estado inicial ────────────────────────────────────────────────────────────

const FORM_INICIAL = {
  descripcion: '',
  idDelegacion: '',
  idTipoProfesional: '',
  idFiguraProfesional: '',
  idClaseMonitoreo: '',
  importe: '',
  fechaExpira: '',
  activo: true,
  observacion: '',
  idsProfesionalesIncluidos: [],
};

// ── Columnas grilla incluidos ─────────────────────────────────────────────────

const buildColumnasIncluidos = (puedeEditarProfesionales, onEliminar) =>
  generateColumnsDefaultConfiguration([
    { field: 'id', headerName: 'Id', width: 80 },
    { field: 'apellido', headerName: 'Apellido', width: 180 },
    { field: 'nombre', headerName: 'Nombre', width: 180 },
    { field: 'tipoDocumento', headerName: 'T. de Documento', width: 140 },
    { field: 'numeroDoc', headerName: 'Número', width: 120 },
    {
      field: 'acciones', headerName: '', width: 60, sortable: false,
      renderCell: ({ row }) =>
        puedeEditarProfesionales ? (
          <Tooltip title='Quitar'>
            <IconButton size='small' onClick={() => onEliminar(row.id)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ]);

const ID_ROL_DIRECTOR_MEDICO_SEGURIDAD = 23;

const getFieldGridSize = (col) => ({
  xs: col > 12 ? 100 : 12,
  sm: col > 12 ? 50 : col === 12 ? 12 : 6,
  lg: col,
});

// ── Componente ────────────────────────────────────────────────────────────────

export const HonorarioFidelizadoCreationContent = ({ setChildrenAction }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { idHonorarioFidelizado, isNew, allowedWrite, delegaciones, tipoProfesionales } =
    location.state;

  const [formData, setFormData] = useState(FORM_INICIAL);
  const [figurasProfesional, setFigurasProfesional] = useState([]);
  const [clasesMonitoreo, setClasesMonitoreo] = useState([]);
  const [profIncluidos, setProfIncluidos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  // ── Combos ──────────────────────────────────────────────────────────────

  const { data: dataFiguras } = useQuery(GET_ALL_FIGURA_PROFESIONAL);
  useEffect(() => {
    if (!dataFiguras) return;
    setFigurasProfesional(adaptFiguraProfesionalArray(dataFiguras));
  }, [dataFiguras]);

  const { data: dataCalificaciones } = useQuery(GET_ALL_CALIFICACIONES_MONITOREO);
  useEffect(() => {
    if (!dataCalificaciones) return;
    setClasesMonitoreo(adaptClasificacionesMonitoreoArray(dataCalificaciones));
  }, [dataCalificaciones]);

  const { data: dataRoles } = useQuery(GET_ROLES_BY_USER, { fetchPolicy: 'no-cache' });

  const esDirectorMedicoSeguridad = useMemo(() => {
    const roles = dataRoles?.rolesByUserId ?? [];
    return roles.some(r => r.id === ID_ROL_DIRECTOR_MEDICO_SEGURIDAD);
  }, [dataRoles]);

  const puedeEditarProfesionales = allowedWrite;

  // ── renderInputGroups (mismo patrón que HonorarioCreationContent) ────────

  // Outside the component, or right above it — pure helper, no closures over hooks
  const updateFieldOptions = (field, { delegaciones, tipoProfesionales, figurasFiltradas, clasesMonitoreo, isNew }) => {
    const updated = { ...field };
    switch (field.name) {
      case 'idDelegacion':
        updated.options = delegaciones;
        updated.disabled = !isNew;
        break;
      case 'idTipoProfesional':
        updated.options = tipoProfesionales;
        updated.disabled = !isNew;
        break;
      case 'idFiguraProfesional':
        updated.options = figurasFiltradas;
        updated.disabled = !isNew;
        break;
      case 'idClaseMonitoreo':
        updated.options = clasesMonitoreo;
        updated.disabled = !isNew;
        break;
      default:
        break;
    }

    if(esDirectorMedicoSeguridad || !allowedWrite)
      updated.disabled = true;

    return updated;
  };

  const mapGroupFields = (group, context) => ({
    ...group,
    fields: group.fields.map((field) => updateFieldOptions(field, context)),
  });

  // Inside the component:
  const renderInputGroups = useMemo(() => {
    const figurasFiltradas = figurasProfesional.filter(
      (f) => f.idTipoProfesional === formData.idTipoProfesional
    );
    const delegacionesFiltradas = delegaciones?.filter((d) => d.displayName !== 'TODAS') ?? [];
    const tipoProfesionalesFiltradas = tipoProfesionales?.filter((d) => d.displayName !== 'TODOS') ?? [];

    const context = {
      delegaciones: delegacionesFiltradas,
      tipoProfesionales: tipoProfesionalesFiltradas,
      figurasFiltradas,
      clasesMonitoreo,
      isNew,
    };

    return structuredClone(inputGroups).map((group) => mapGroupFields(group, context));
  }, [inputGroups, figurasProfesional, formData.idTipoProfesional, delegaciones, tipoProfesionales, clasesMonitoreo, isNew]);

  // ── Carga de datos para edición ──────────────────────────────────────────

  const [getHonorario, { data: dataHonorario, loading: getLoading }] = useLazyQuery(
    GET_HONORARIOS_FIDELIZADOS,
    { fetchPolicy: 'no-cache' }
  );

  useEffect(() => {
    if (!dataHonorario) return;
    const todos = dataHonorario?.honorariosFidelizados ?? [];
    const honorario = todos.find((h) => h.idHonorarioFidelizado === idHonorarioFidelizado);
    if (!honorario) return toast.error('No se pudo cargar la información del honorario.');

    const adapted = adaptHonorarioFidelizado(honorario);
    const idsIncluidos = adapted.profesionalesIncluidos.map((p) => p.idProfesional);

    setFormData({
      idHonorarioFidelizado: adapted.id,
      descripcion: adapted.descripcion,
      idDelegacion: adapted.idDelegacion,
      idTipoProfesional: adapted.idTipoProfesional,
      idFiguraProfesional: adapted.idFiguraProfesional,
      idClaseMonitoreo: adapted.idClaseMonitoreo,
      importe: adapted.importe,
      fechaExpira: adapted.fechaExpira?.split('T')[0] ?? '',
      activo: adapted.activo,
      observacion: adapted.observacion ?? '',
      idsProfesionalesIncluidos: idsIncluidos,
    });

    const incluidos = adapted.profesionalesIncluidos.map((pe) => ({
      id: pe.idProfesional,
      apellido: pe.idProfesionalNavigation?.apellido ?? '',
      nombre: pe.idProfesionalNavigation?.nombre ?? '',
      tipoDocumento: pe.idProfesionalNavigation?.idTipoDocumentoNavigation?.descripcion ?? '-',
      numeroDoc: pe.idProfesionalNavigation?.numeroDoc ?? '-',
    }));
    setProfIncluidos(incluidos);
  }, [dataHonorario]);

  useEffect(() => {
    if (!idHonorarioFidelizado || isNew) return;
    getHonorario({ variables: { idDelegacion: null, idTipoProfesional: null, activo: true } });
  }, [idHonorarioFidelizado]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const [createHonorario, { loading: createLoading }] = useMutation(CREATE_HONORARIO_FIDELIZADO);
  const [updateHonorario, { loading: updateLoading }] = useMutation(UPDATE_HONORARIO_FIDELIZADO);

  const saveLoading = useMemo(() => createLoading || updateLoading, [createLoading, updateLoading]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleChange = useCallback((name, value) => {
    if (name === 'idTipoProfesional') {
      setFormData((prev) => ({ ...prev, idFiguraProfesional: '', [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleSave = async () => {
    const camposRequeridos = [
      { value: formData.descripcion?.trim(), message: 'Debe ingresar una descripción.' },
      { value: formData.idDelegacion, message: 'Debe seleccionar una delegación.' },
      { value: formData.idTipoProfesional, message: 'Debe seleccionar un tipo de profesional.' },
      { value: formData.idFiguraProfesional, message: 'Debe seleccionar una figura profesional.' },
      { value: formData.idClaseMonitoreo, message: 'Debe seleccionar una clase de monitoreo.' },
      { value: formData.importe, message: 'Debe ingresar un importe.' },
      { value: formData.fechaExpira, message: 'Debe ingresar una fecha de expiración.' },
    ];

    const errores = camposRequeridos.filter((c) => !c.value && c.value !== 0).map((c) => c.message);
    if (errores.length) return toast.error(errores.join('\n'));

    try {
      if (isNew) {
        const { data } = await createHonorario({ variables: { input: buildCreateInput(formData) } });
        if (!data?.createHonorarioFidelizado) return;
        toast.success('Honorario fidelizado creado con éxito.');
      } else {
        const { data } = await updateHonorario({ variables: { input: buildEditInput(formData) } });
        if (!data?.updateHonorarioFidelizado) return;
        toast.success('Honorario fidelizado actualizado con éxito.');
      }
      navigate('/honorarios/fidelizados');
    } catch (error) {
      const mensaje = error?.graphQLErrors?.[0]?.message ?? error?.message ?? 'Ocurrió un error inesperado.';
      toast.error(mensaje);
    }
  };

  // ── Profesionales incluidos ───────────────────────────────────────────────

  const handleEliminarIncluido = useCallback((idProfesional) => {
    setProfIncluidos((prev) => prev.filter((p) => p.id !== idProfesional));
    setFormData((prev) => ({
      ...prev,
      idsProfesionalesIncluidos: prev.idsProfesionalesIncluidos.filter((id) => id !== idProfesional),
    }));
  }, []);

  const handleAceptarDialog = useCallback((idsSeleccionados, profesionalesDelDialog) => {
    setDialogOpen(false);
    setProfIncluidos((prev) => {
      const prevMantener = prev.filter((p) => idsSeleccionados.includes(p.id));
      const prevIds = prevMantener.map((p) => p.id);
      const nuevos = profesionalesDelDialog.filter((p) => !prevIds.includes(p.id));
      return [...prevMantener, ...nuevos];
    });
    setFormData((prev) => ({ ...prev, idsProfesionalesIncluidos: idsSeleccionados }));
  }, []);

  const columnasIncluidos = useMemo(
    () => buildColumnasIncluidos(puedeEditarProfesionales, handleEliminarIncluido),
    [puedeEditarProfesionales, handleEliminarIncluido]
  );

  // ── Botón header ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!setChildrenAction) return;
    setChildrenAction(() => (
      <div className='header-actions-calidad'>
        {idHonorarioFidelizado > 0 && (
          <div className='chip-estado-calidad'>
            <p>Id: <strong>{idHonorarioFidelizado}</strong></p>
          </div>
        )}
        <div className='top-buttons'>
          {allowedWrite && (
            <CustomButton
              label='Guardar'
              onClick={handleSave}
              isLoading={saveLoading}
              disabled={saveLoading}
              variant='contained'
            />
          )}
        </div>
      </div>
    ));
  }, [setChildrenAction, puedeEditarProfesionales, idHonorarioFidelizado, formData, saveLoading]);

  if (getLoading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '20vh' }}>
        <CircularProgress size={40} />
      </Box>
    );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <Grid container spacing={2}>
        {renderInputGroups.map((group, index) => (
          <GroupedContainer
            key={`input-group-${index}`}
            ungrouped={!group.name}
            sectionTitle={group.name}
          >
            <Grid container spacing={2} size={{ xs: 100 }}>
              {group.fields.map((field, fieldIndex) => (
                !field.hidden && (
                  <Grid
                    key={`group_${index}-field_${fieldIndex}`}
                    size={getFieldGridSize(field.col)}
                  >
                    <RenderInputByTypeMobile
                      input={field}
                      handleChange={handleChange}
                      value={formData[field.name]}
                    />
                  </Grid>
                )
              ))}
            </Grid>
          </GroupedContainer>
        ))}

        {/* Profesionales incluidos — ahora entre los grupos y observación */}
        <GroupedContainer ungrouped>
          <Grid size={{ xs: 100 }} sx={{ mt: 1 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Profesionales incluidos</strong>
            </div>
            {puedeEditarProfesionales && (
              <Box sx={{ mb: 2 }}>
                <CustomButton
                  label='Profesional incluido'
                  variant='outlined'
                  startIcon={<PersonAddIcon />}
                  disabled={!formData.idTipoProfesional}
                  onClick={() => setDialogOpen(true)}
                />
              </Box>
            )}
            <DataGridGenerico
              columns={columnasIncluidos}
              rows={profIncluidos}
              totalCount={profIncluidos.length}
              toolbar={false}
              autoHeight
            />
          </Grid>
        </GroupedContainer>
      </Grid>

      <SelectorProfesionalesDialog
        open={dialogOpen}
        idTipoProfesional={formData.idTipoProfesional}
        idDelegacion={formData.idDelegacion || null}
        idClaseMonitoreo={formData.idClaseMonitoreo || null}
        seleccionados={formData.idsProfesionalesIncluidos}
        onCerrar={() => setDialogOpen(false)}
        onAceptar={handleAceptarDialog}
      />
    </div>
  );
};